import { Controller } from 'stimulus';
import { FetchRequest } from '@rails/request.js';

export default class extends Controller {
  static targets = ['durationSelect', 'customRange', 'fromDate', 'toDate', 'hoursDisplay', 'param'];
  static values = { hoursUrl: String };
  static outlets = ['billable-tax'];

  connect() {
    this.pendingFetch = null;
  }

  disconnect() {
    this.pendingFetch?.abort();
    this.pendingFetch = null;
  }

  scopeChanged(event) {
    if (event.target.value === 'all_uninvoiced') {
      this.durationSelectTarget.disabled = true;
      this.#showCustomRange(false);
      this.#fetchHours('all_uninvoiced');
    } else {
      this.durationSelectTarget.disabled = false;
      this.durationChanged();
    }
  }

  durationChanged() {
    const duration = this.durationSelectTarget.value;
    this.#showCustomRange(duration === 'custom');

    if (duration !== 'custom') {
      this.#fetchHours(duration);
    } else if (this.#hasCustomRange()) {
      this.#fetchHours(this.#customRange());
    } else {
      this.pendingFetch?.abort();
      this.#renderHours('');
      this.#dateField(this.fromDateTarget).querySelector('input[type="text"]').focus();
    }
  }

  dateChanged(event) {
    if (event.target !== this.fromDateTarget && event.target !== this.toDateTarget) {
      return;
    }

    this.#dateField(this.toDateTarget).setAttribute(
      'data-ui--date-field-min-value',
      this.fromDateTarget.value
    );
    this.#dateField(this.fromDateTarget).setAttribute(
      'data-ui--date-field-max-value',
      this.toDateTarget.value
    );

    if (this.#hasCustomRange()) {
      this.#fetchHours(this.#customRange());
    } else {
      this.pendingFetch?.abort();
      this.#renderHours('');
    }
  }

  prepare() {
    this.paramTargets.forEach((param) => param.remove());

    const params = new FormData();
    this.#appendDuration(params, this.#selectedDuration());
    params.append('tax', this.#taxAllChecked());

    this.element.append(...[...params].map(([name, value]) => this.#hiddenInput(name, value)));
  }

  #showCustomRange(shown) {
    this.customRangeTarget.hidden = !shown;
    this.customRangeTarget.disabled = !shown;
  }

  #customRange() {
    return [this.fromDateTarget.value, this.toDateTarget.value];
  }

  #hasCustomRange() {
    return this.#customRange().every(Boolean);
  }

  async #fetchHours(duration) {
    this.pendingFetch?.abort();
    const { signal } = (this.pendingFetch = new AbortController());

    this.#renderHours('');

    const url = new URL(this.hoursUrlValue, window.location.origin);
    this.#appendDuration(url.searchParams, duration);

    try {
      const request = new FetchRequest('get', url.toString(), { responseKind: 'json', signal });
      const response = await request.perform();
      const data = await response.json;
      if (signal.aborted) {
        return;
      }
      this.#renderHours(data.billable_hours);
    } catch (error) {
      if (signal.aborted) {
        return;
      }
      console.error(error);
    }
  }

  #renderHours(value) {
    this.hoursDisplayTarget.textContent = value;
    this.#submitButton.disabled = value === '' || value === '00:00';
  }

  #dateField(target) {
    return target.closest('[data-controller~="ui--date-field"]');
  }

  get #submitButton() {
    return [...this.element.elements].find((element) => element.type === 'submit');
  }

  #selectedDuration() {
    if (this.element.querySelector('#tracked-hours-all-uninvoiced').checked) {
      return 'all_uninvoiced';
    }

    const duration = this.durationSelectTarget.value;
    if (duration === 'custom') {
      return this.#customRange();
    }
    return duration;
  }

  #appendDuration(params, duration) {
    if (Array.isArray(duration)) {
      duration.forEach((date) => params.append('duration[]', date));
    } else {
      params.append('duration', duration);
    }
  }

  #hiddenInput(name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    input.dataset.billableTrackedHoursTarget = 'param';
    return input;
  }

  #taxAllChecked() {
    return this.hasBillableTaxOutlet ? this.billableTaxOutlet.allItemsTaxable : false;
  }
}
